// Puts `mcp-narrow--dark` on <html> while the narrow MCP card (base mobile
// layout) shows under a dark host theme; app/css/mcp-narrow.css keys off it.
import {
  isMcpAppSurface,
  isMcpNarrowLayout,
  mcpOpenAiTheme,
  mcpThemeSlug,
} from "@/modules/mcpSurface";
import { onMcpThemeChange } from "@/modules/mcpAppLink";

const DARK_CLASS = "mcp-narrow--dark";

let started = false;
// The latest theme the host reported live; falls back to what the embed URL
// said, then to the viewer's colour scheme (as McpPlayer's CSS does).
let liveTheme = "";

function currentTheme(): string {
  const t = liveTheme || mcpThemeSlug();
  if (t) {
    return t;
  }
  try {
    return window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  } catch {
    return "light";
  }
}

function apply() {
  const dark = isMcpNarrowLayout() && currentTheme() === "dark";
  document.documentElement.classList.toggle(DARK_CLASS, dark);
}

export function startMcpNarrowTheme() {
  if (started || typeof window === "undefined" || !isMcpAppSurface()) {
    return;
  }
  started = true;
  apply();

  let raf = 0;
  window.addEventListener("resize", () => {
    if (raf) {
      return;
    }
    raf = window.requestAnimationFrame(() => {
      raf = 0;
      apply();
    });
  });
  onMcpThemeChange((theme: string) => {
    liveTheme = theme;
    apply();
  });
  // Only matters while no theme has been reported, but is harmless otherwise:
  // apply() re-reads the same priority order. `addListener` is for Safari < 14.
  try {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    if (mq.addEventListener) {
      mq.addEventListener("change", apply);
    } else if (mq.addListener) {
      mq.addListener(apply);
    }
  } catch {
    /* matchMedia unavailable: nothing to follow */
  }
  window.addEventListener("openai:set_globals", () => {
    const t = mcpOpenAiTheme();
    if (t) {
      liveTheme = t;
      apply();
    }
  });
}
